/**
 * List overflow / "show N more" - same family as Browse focus "Show N hidden".
 *
 * Primary list: activity-ordered cap (default 5, matches PIP_CAP).
 * Overflow: ShowOverflowLink until expanded.
 *
 * Gallery: /?test=maater-ui#list-overflow
 */

/** Default primary rows before "Show N more" (aligned with workspace pip cap). */
export const LIST_OVERFLOW_PRIMARY_CAP = 5

export type ListOverflowPartition<T> = {
  primary: T[]
  overflow: T[]
  overflowCount: number
}

/**
 * Split an already-ordered list into primary + overflow.
 * When `expanded` or `forceAll`, primary is the full list.
 * `overflowCount` stays the would-be hidden count so "Show less" can remain.
 */
export function partitionListOverflow<T>(
  ordered: T[],
  options: {
    cap?: number
    expanded?: boolean
    /** e.g. filter active - show every match */
    forceAll?: boolean
  } = {},
): ListOverflowPartition<T> {
  const cap = options.cap ?? LIST_OVERFLOW_PRIMARY_CAP
  const wouldOverflow = Math.max(0, ordered.length - cap)
  if (options.forceAll || ordered.length <= cap) {
    return { primary: ordered, overflow: [], overflowCount: 0 }
  }
  if (options.expanded) {
    return { primary: ordered, overflow: [], overflowCount: wouldOverflow }
  }
  return {
    primary: ordered.slice(0, cap),
    overflow: ordered.slice(cap),
    overflowCount: wouldOverflow,
  }
}

/** Order ids by recency map (newest first); unknown ids sort last, stable by id. */
export function orderIdsByRecencyMap(
  ids: string[],
  recency: Record<string, number>,
): string[] {
  return [...ids].sort((a, b) => {
    const da = recency[a] ?? 0
    const db = recency[b] ?? 0
    if (db !== da) return db - da
    return a.localeCompare(b)
  })
}

/** "Show 3 more" / "Show 1 more" */
export function formatShowMoreCountLabel(count: number, noun = 'more'): string {
  if (count <= 0) return `Show ${noun}`
  return `Show ${count} ${noun}`
}

/** Workspace picker / Browse-style overflow copy. */
export function formatShowMoreWorkspacesLabel(count: number): string {
  if (count === 1) return 'Show 1 more workspace'
  return `Show ${count} more workspaces`
}

export function formatShowLessLabel(): string {
  return 'Show less'
}

/**
 * Picker candidates: honor org `showInSidebar`, but always keep currently
 * active ids so users can turn a hidden workspace off.
 */
export function filterSidebarPickerWorkspaces<T extends { id: string; showInSidebar?: boolean }>(
  workspaces: T[],
  activeIds: string[],
): T[] {
  const active = new Set(activeIds)
  return workspaces.filter((w) => w.showInSidebar !== false || active.has(w.id))
}

/**
 * Workspace picker order: currently active first (MRU among active), then
 * inactive (MRU among inactive). Prevents the live scope from falling under
 * "Show N more" when older inactive orgs have fresher touch timestamps.
 */
export function orderWorkspacesForPicker<T extends { id: string }>(
  workspaces: T[],
  activeIds: string[],
  orderIdsByRecency: (ids: string[]) => string[],
): T[] {
  const byId = new Map(workspaces.map((w) => [w.id, w]))
  const activeSet = new Set(activeIds)
  const activeOrdered = orderIdsByRecency(
    workspaces.filter((w) => activeSet.has(w.id)).map((w) => w.id),
  )
  const inactiveOrdered = orderIdsByRecency(
    workspaces.filter((w) => !activeSet.has(w.id)).map((w) => w.id),
  )
  return [...activeOrdered, ...inactiveOrdered]
    .map((id) => byId.get(id))
    .filter((w): w is T => w != null)
}
